import { useContext } from 'react'
import type { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'
import { PATHS } from '@/app/router/paths'
import {
  BreathIcon,
  JournalIcon,
  MenuIcon,
  MoreIcon,
  PlannerIcon,
  RememberIcon,
  TodayIcon,
} from '@/components/icons'
import type { IconProps } from '@/components/icons'
import { cx } from '@/components/ui/internal/cx'
import { NavCollapseContext } from './nav-collapse-context'
import { useNavCollapse } from './use-nav-collapse'
import type { NavCollapseState } from './use-nav-collapse'
import './navigation.css'

type NavItem = {
  label: string
  to: string
  icon: (props: IconProps) => ReactElement
}

/** Contract §7 order: Vandaag / Onthouden / Planner / Adem / Dagboek / Meer. */
const ITEMS: NavItem[] = [
  { label: 'Vandaag', to: PATHS.app.today, icon: TodayIcon },
  { label: 'Onthouden', to: PATHS.app.remember, icon: RememberIcon },
  { label: 'Planner', to: PATHS.app.planner, icon: PlannerIcon },
  { label: 'Adem', to: PATHS.app.breathing, icon: BreathIcon },
  { label: 'Dagboek', to: PATHS.app.journal, icon: JournalIcon },
  { label: 'Meer', to: PATHS.app.more, icon: MoreIcon },
]

function BottomNavView({ state }: { state: NavCollapseState }) {
  if (state.collapsed) {
    return (
      <button type="button" className="bottom-nav__open" onClick={state.toggle} aria-expanded={false}>
        <MenuIcon size={18} />
        open menu
      </button>
    )
  }

  return (
    <nav className="bottom-nav" aria-label="Hoofdnavigatie">
      <button
        type="button"
        className="bottom-nav__collapse"
        onClick={state.toggle}
        aria-expanded={true}
        aria-controls="bottom-nav-list"
      >
        Verbergen
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9.5l6 6 6-6" />
        </svg>
      </button>
      <ul className="bottom-nav__list" id="bottom-nav-list">
        {ITEMS.map((item) => (
          <li key={item.to} className="bottom-nav__entry">
            <NavLink
              to={item.to}
              className={({ isActive }) => cx('bottom-nav__item', isActive && 'bottom-nav__item--active')}
            >
              <item.icon size={22} />
              <span className="bottom-nav__label">{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/**
 * Client bottom navigation: six labelled items (≥44px targets), collapse
 * control 'Verbergen' half on/half above the nav, collapsed 'open menu'
 * floating 4px above the bottom safe area. Keyboard and screen-reader
 * operable in both states (real buttons, aria-expanded).
 */
export function BottomNav() {
  const context = useContext(NavCollapseContext)
  if (context) return <BottomNavView state={context} />
  return <StandaloneBottomNav />
}

function StandaloneBottomNav() {
  const state = useNavCollapse()
  return <BottomNavView state={state} />
}
